import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerResearchApi,
  type BuyerResearchBriefDto,
  type BuyerResearchCandidateDto,
  type BuyerResearchCandidateListQuery,
  type BuyerResearchCandidatePageDto,
  type BuyerResearchRunDto,
} from "@/api/buyerResearch";
import { isSessionExpired, retryUnlessSessionExpired } from "@/api/polling";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { elapsedLabel, isTerminalStatus } from "./researchFormat";

/** How often an in-flight run is re-read (see {@link useResearchRun}). */
export const RESEARCH_POLL_MS = 5000;

export function useResearchRuns(buyerLeadId: string) {
  return useQuery({
    queryKey: buyerKeys.researchRuns(buyerLeadId),
    queryFn: () => buyerResearchApi.listRuns(buyerLeadId),
    enabled: Boolean(buyerLeadId),
  });
}

/**
 * One run, polled every {@link RESEARCH_POLL_MS} while it is in flight —
 * Queued, Running, PullQueued, Pulling or Refining ({@link isTerminalStatus})
 * — and left alone once it reaches a terminal status. The query
 * otherwise inherits the app's five-minute `staleTime` and never re-reads, so
 * without the interval a finished run would keep showing its progress list
 * indefinitely. Polling stops on an expired session so an unattended tab does
 * not 401 forever.
 *
 * <p>🔴 <b>IT ASKS FOR THE RUN WITHOUT ITS CANDIDATES (R5).</b> The list is its
 * own read — {@link useResearchCandidatePage} — filtered, sorted and paged by the
 * server. A run carrying every row on every poll was about 1.8 MB, re-fetched
 * every five seconds while a pull ran; nothing on this read's consumers reads
 * `candidates` any more, so the empty array the server sends is never
 * mistaken for an empty run.</p>
 */
export function useResearchRun(buyerLeadId: string, runId: string | null) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: buyerKeys.researchRun(buyerLeadId, runId ?? ""),
    queryFn: () => buyerResearchApi.getRun(buyerLeadId, runId!, { includeCandidates: false }),
    enabled: Boolean(buyerLeadId && runId),
    refetchInterval: (query) => {
      if (isSessionExpired(query.state.error)) return false;
      const d: BuyerResearchRunDto | undefined = query.state.data;
      return d && !isTerminalStatus(d.run.status) ? RESEARCH_POLL_MS : false;
    },
    retry: retryUnlessSessionExpired,
  });

  /**
   * 🔴 <b>THIS POLL IS THE ONLY THING THAT LEARNS A RUN HAS STOPPED, AND IT
   * ONLY TOLD THE DETAIL.</b> A run reaches its terminal status on the SERVER,
   * so there is no client write for the seven mutations below to hang an
   * invalidation off. The run LIST has no interval of its own, the QueryClient
   * runs at `staleTime: 5min` with `refetchOnWindowFocus: false`, and this poll
   * stops the instant the status goes terminal — so without this the history
   * row keeps its `Running` badge, its mid-flight counts and its part-spent cost
   * for MINUTES beside a body already rendering the finished run, with no
   * self-recovery. One invalidation, at the transition, closes it.
   *
   * <p>The test is the run we last saw IN FLIGHT, not a bare "is terminal", and
   * the difference is what makes it fire once and only for a run this hook
   * actually watched stop:</p>
   *
   * <ul>
   * <li><b>A run already terminal on first sight</b> — the common case, since
   * opening the tab selects the newest run and most are done — never matches,
   * so reading history invalidates nothing.</li>
   * <li><b>Every later tick and every re-render</b> re-runs this with the same
   * terminal status; the first fire clears the id, so the rest find nothing to
   * do. That is also what makes it idempotent under the double-invoked effects
   * of StrictMode, which `main.tsx` really does mount the app in.</li>
   * <li><b>Selecting another run</b> moves the id out from under the comparison,
   * so a run left mid-flight cannot be credited to the terminal one the
   * specialist switched to.</li>
   * </ul>
   *
   * <p>🔴 <b>THIS IS NOW THE HAPPY PATH FOR A PULL AND A CONFIRMED REFINEMENT
   * (R9).</b> Both requests only QUEUE the work and answer with the run in
   * `PullQueued` / `Refining`; the worker buys and scores afterwards, so the
   * batch lands while this poll is watching and the mutation's own invalidation
   * has long since run. The transition invalidation below is what brings the
   * new rows onto the list.</p>
   *
   * <p>The blast radius is the run list AND this run's candidate list,
   * deliberately not `research(buyerId)`: the detail is the query that just
   * observed the transition, so it is the one read on the tab already fresh, and
   * covering its whole key would buy a second round-trip for data we are
   * holding. The candidate list is the exception because it is a SEPARATE read
   * (R5): a pull or a refinement that the worker finished moved the rows, and
   * the page on screen was read before it did.</p>
   */
  const status = query.data?.run.status ?? null;
  const inFlightRunId = useRef<string | null>(null);
  useEffect(() => {
    if (!runId || status == null) return;
    if (!isTerminalStatus(status)) {
      inFlightRunId.current = runId;
      return;
    }
    if (inFlightRunId.current !== runId) return;
    inFlightRunId.current = null;
    void qc.invalidateQueries({ queryKey: buyerKeys.researchRuns(buyerLeadId) });
    void qc.invalidateQueries({ queryKey: buyerKeys.researchCandidates(buyerLeadId, runId) });
  }, [buyerLeadId, runId, status, qc]);

  return query;
}

/**
 * One page of the run's candidates, as the SERVER filtered, sorted and paged
 * them (R5). The client never sorts: there is one ordering, in the service.
 *
 * <p>The previous page stays on screen while a filter change is in flight,
 * rather than dropping the table to a skeleton on every chip press — the
 * counts beside it say which read they belong to. 🔴 <b>Only within ONE
 * run:</b> the page carries no run id, so holding the last run's rows while a
 * different run loads would put one run's candidates under another run's
 * header. A run switch shows the skeleton instead.</p>
 */
export function useResearchCandidatePage(
  buyerLeadId: string,
  runId: string,
  query: BuyerResearchCandidateListQuery,
  enabled = true,
  live = false,
) {
  const runKey = buyerKeys.researchCandidates(buyerLeadId, runId);
  const runPrefix = JSON.stringify(runKey);
  const runPrefixLength = runKey.length;
  return useQuery({
    queryKey: buyerKeys.researchCandidatePage(buyerLeadId, runId, query),
    queryFn: () => buyerResearchApi.listCandidates(buyerLeadId, runId, query),
    enabled: Boolean(buyerLeadId && runId) && enabled,
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      if (!key) return undefined;
      const prefix = JSON.stringify(key.slice(0, runPrefixLength));
      return prefix === runPrefix ? previous : undefined;
    },
    // 🔴 RG23 — WHILE THE RUN IS STILL SCORING, THE PAGE RE-READS ON THE RUN'S
    // OWN FIVE-SECOND POLL. The worker saves each group of twenty the moment it
    // is scored, so the list is where the specialist watches a batch arrive.
    // The key does not change on a refresh, so the page, the filters and the
    // sort stay exactly where the reader left them and the rows already on
    // screen do not blink; the new ones slot into the server's order. Stops on
    // its own when the caller says the run is finished, and on an expired
    // session like every sibling poll here.
    refetchInterval: (q) =>
      live && !isSessionExpired(q.state.error) ? RESEARCH_POLL_MS : false,
    retry: retryUnlessSessionExpired,
  });
}

/**
 * One candidate's full record — reasons, both fit groups, contact, provenance —
 * fetched only when its row is expanded. The row itself carries none of it.
 */
export function useResearchCandidate(
  buyerLeadId: string,
  runId: string,
  candidateId: string,
) {
  return useQuery({
    queryKey: buyerKeys.researchCandidate(buyerLeadId, runId, candidateId),
    queryFn: () => buyerResearchApi.getCandidate(buyerLeadId, runId, candidateId),
    enabled: Boolean(buyerLeadId && runId && candidateId),
    retry: retryUnlessSessionExpired,
  });
}

export function useResearchDismissals(buyerLeadId: string, enabled: boolean) {
  return useQuery({
    queryKey: buyerKeys.researchDismissals(buyerLeadId),
    queryFn: () => buyerResearchApi.listDismissals(buyerLeadId),
    enabled: Boolean(buyerLeadId) && enabled,
  });
}

/**
 * Every research write invalidates the whole buyer prefix. The reads under
 * `research` are the obvious targets, but an attach also creates an
 * association — the shortlist under `associatedListings` moves, and so does
 * the detail record whose counts it feeds — so the blast radius is the same
 * one the Associated listings tab already uses for a tie.
 */
export function useStartResearchRun(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brief: BuyerResearchBriefDto) => buyerResearchApi.startRun(buyerLeadId, brief),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * Quick search — the typed sentence is read on the server and a run is queued
 * for it in the same request. Invalidates the whole buyer prefix, like
 * {@link useStartResearchRun}: the run list moves, and nothing else is written
 * into the cache by hand. A refused sentence is a 400 and creates no run, so
 * there is nothing to invalidate on that path.
 */
export function useStartQuickSearch(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => buyerResearchApi.startQuickSearch(buyerLeadId, text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

export function useCancelResearchRun(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => buyerResearchApi.cancelRun(buyerLeadId, runId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * "Pull more" — QUEUE a buy of the run's NEXT page (R9). The request answers at
 * once with the run in `PullQueued` and nothing bought; the worker buys, ranks
 * and appends, and {@link useResearchRun}'s poll watches it happen. A pull that
 * fails or never starts says so on the SOURCE LOG, not as an error from this
 * request.
 *
 * 🔴 <b>The response is WRITTEN into this run's detail AND everything is still
 * invalidated.</b> Written because the response is the queued state, and
 * without it the button returns from pending to "Pull more" over the stale
 * Completed run for the length of the refetch — offering a second pull the
 * server would refuse as busy. Invalidated because the run LIST's status moved
 * too, and a `setQueryData` on the detail alone would leave that list behind.
 * The detail read carries no candidates (R5), so the written value carries none
 * either, and the list keeps its own read.
 *
 * <b>The refusal is a 400 carrying a blocker CODE</b> — read it with
 * `pullRefusalBlocker`, never from the message.
 */
export function usePullMoreResearch(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => buyerResearchApi.pullMore(buyerLeadId, runId),
    onSuccess: (data, runId) => {
      writeRunDetail(qc, buyerLeadId, runId, data);
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * Put a write's returned run into the detail cache, as the detail read would
 * have returned it: without candidates (R5). Skipped for a response that is not
 * a run, so a malformed answer can never become the thing the poll reads.
 */
function writeRunDetail(
  qc: ReturnType<typeof useQueryClient>,
  buyerLeadId: string,
  runId: string,
  data: BuyerResearchRunDto | undefined,
) {
  if (data?.run == null) return;
  qc.setQueryData<BuyerResearchRunDto>(buyerKeys.researchRun(buyerLeadId, runId), {
    ...data,
    candidates: [],
  });
}

/**
 * Show the ranked rows a run bought, scored and held back.
 *
 * 🔴 <b>It buys nothing, and that is what makes it a different control from
 * "Pull more".</b> The records are already spent and the model has already
 * scored these candidates; the only cost is LATER, because a refinement
 * re-scores what is shown. A specialist who asks for the rest accepts that on
 * the one run they asked about — which is why it was an opt-in rather than a
 * larger shown cut. (Since R1 there is no cut; only runs scored before it, or
 * rows a refinement promoted, have anything to reveal.)
 *
 * Invalidates rather than writing the response into the cache, for the reason
 * every sibling here does: the same call moves the run's own ShownCount and its
 * withheld state, and a client that patched one field would be re-deriving the
 * others.
 */
export function useRevealWithheldResearch(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => buyerResearchApi.revealWithheld(buyerLeadId, runId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * Area C — reject what the probe did to a run's query.
 *
 * 🔴 <b>It applies to the buyer's NEXT run and un-buys nothing.</b> Nothing on
 * this run moves: its records are spent, its candidates stay, and the filter it
 * searched stays on the record as the account of what the money bought. The
 * caller must say so BEFORE the press, not after.
 *
 * Invalidates rather than writing the response into the cache, for the reason
 * every sibling here does: the same call changes what the run summary reports
 * about itself, and a client that patched one field would be re-deriving the
 * rest.
 *
 * <b>The refusal is a 400 carrying a blocker CODE</b> — read it with
 * `revertRefusalBlocker`, never from the message.
 */
export function useRevertQueryChanges(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => buyerResearchApi.revertQueryChanges(buyerLeadId, runId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * Ask Phoenix — classify a refinement a specialist typed.
 *
 * 🔴 <b>It spends nothing on its own.</b> A re-query stops at `awaiting
 * confirmation` holding a MEASURED match count, and records are bought only by
 * {@link useConfirmRefinement}. That is the whole of the feature's first closed
 * decision: free count first, then ask.
 *
 * The call is QUEUED, so this resolving means the refinement row exists — not
 * that it has been classified. The run poll in {@link useResearchRun} carries
 * the answer, which is why `Refining` is a non-terminal status.
 */
export function useRefineRun(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { runId: string; text: string }) =>
      buyerResearchApi.refine(buyerLeadId, v.runId, v.text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * Approve the spend a classified re-query is waiting on — the click decision 1
 * exists for. The count was free, the number is on screen, and nothing has been
 * bought until the specialist presses.
 *
 * 🔴 <b>QUEUED (R9).</b> The request answers with the refinement `confirmed`
 * and the run `Refining`; the worker buys afterwards, and a failure lands on
 * the refinement's `error` rather than on this request. The response is
 * written into the detail for the reason {@link usePullMoreResearch} gives, and
 * everything is still invalidated.
 */
export function useConfirmRefinement(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { runId: string; refinementId: string }) =>
      buyerResearchApi.confirmRefinement(buyerLeadId, v.runId, v.refinementId),
    onSuccess: (data, v) => {
      writeRunDetail(qc, buyerLeadId, v.runId, data);
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * Clear the applied refinement: every hidden candidate comes back and the run's
 * ORIGINAL search goes back to being the pull target.
 *
 * 🔴 It deletes nothing. The refinement stays in the history with what it did,
 * and any batch it bought stays bought — a cleared refinement is a lens taken
 * off, never a purchase undone.
 */
export function useClearRefinement(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { runId: string; refinementId: string }) =>
      buyerResearchApi.clearRefinement(buyerLeadId, v.runId, v.refinementId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * D2 — the row flips the moment the attach succeeds. The attached row is
 * patched in every cached page and the opened detail of THIS run (one prefix
 * covers both), so it never shows a live ATTACH button between the success and
 * the refetch; the invalidation then brings every other read up to date — the
 * same business on other runs, the Associated listings tab, the counts.
 */
export function useAttachCandidate(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { runId: string; candidateId: string }) =>
      buyerResearchApi.attach(buyerLeadId, args.runId, args.candidateId),
    onSuccess: (result, args) => {
      const patch = <T extends { id: string }>(c: T): T =>
        c.id === result.candidateId
          ? {
              ...c,
              isAttached: true,
              attachedListingId: result.listingId,
              attachedAssociationId: result.associationId,
            }
          : c;
      qc.setQueriesData<BuyerResearchCandidatePageDto | BuyerResearchCandidateDto>(
        { queryKey: buyerKeys.researchCandidates(buyerLeadId, args.runId) },
        (data) => {
          if (!data) return data;
          return "items" in data ? { ...data, items: data.items.map(patch) } : patch(data);
        },
      );
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * D7 — buy the listing broker's contact for one candidate.
 *
 * The response IS the updated candidate, but this deliberately invalidates
 * rather than writing it into the cache: the same call also increments the run's
 * `clefRecordsUsed`, which the run history renders, and a `setQueryData` on the
 * candidate alone would leave that figure a record behind.
 */
export function useFetchBrokerContact(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { runId: string; candidateId: string }) =>
      buyerResearchApi.fetchBrokerContact(buyerLeadId, args.runId, args.candidateId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

export function useDismissCandidate(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { runId: string; candidateId: string; reason?: string | null }) =>
      buyerResearchApi.dismiss(buyerLeadId, args.runId, args.candidateId, args.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

export function useUndismissCandidate(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dismissalId: string) => buyerResearchApi.undismiss(buyerLeadId, dismissalId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/**
 * When THIS PAGE first saw `active` become true, as an ISO string — null while
 * inactive, and reset each time it goes inactive, so the next stretch of work
 * starts its own clock.
 *
 * <p>🔴 <b>A CLIENT OBSERVATION, NOT A SERVER FACT.</b> A queued pull carries
 * no start time of its own on the wire — the run's `startedAt` is the ORIGINAL
 * run's — so the elapsed figure beside it is measured from the moment this page
 * first observed the work. On a page opened part-way through, it is a lower
 * bound on how long the work has really been running — which is why the copy it
 * feeds says "so far" rather than naming a start. Only the pull control reads
 * it; a queued refinement buy shows its status and no clock.</p>
 */
export function useObservedSince(active: boolean): string | null {
  const [since, setSince] = useState<string | null>(() => (active ? new Date().toISOString() : null));
  useEffect(() => {
    setSince((prev) => (active ? (prev ?? new Date().toISOString()) : null));
  }, [active]);
  return active ? since : null;
}

/**
 * Ticks every 15s so a "Running for 2m" label stays current without a heavy
 * timer; null when no start is known or the run is no longer active.
 */
export function useElapsedLabel(since: string | null | undefined, active: boolean): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active || !since) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [active, since]);
  if (!active) return null;
  return elapsedLabel(since, now);
}
